import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { catchError, forkJoin, map } from 'rxjs';
import { DesignFactorsService } from '../../services/design-factors.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { ConnectionSafetyFactorResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { ConnectionSafetyFactorResultsModel } from '../../models/connection-safety-factor-results.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { WellConfigService } from 'src/app/shared/services/well-config.service';

@Component({
  selector: 'app-single-load-connection-safety-factors',
  templateUrl: './single-load-connection-safety-factors.component.html',
  standalone: false
})
export class SingleLoadConnectionSafetyFactorsComponent implements OnInit, OnDestroy {
  private minimumResultType = 'summary';
  private previousView: string;
  private selectedString: string;

  public connectionSafetyFactorResults: ConnectionSafetyFactorResultsModel[];
  public plotName: string;
  public axialResults: any[];
  public loadCases: any[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Minimum', value: 'minimum' },
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public yAxisTitle = '';
  public xAxisTitle = 'Connection Safety Factor';
  public resultsBackup: any[];
  public designFactors: any;
  public userUnits: UserUnitsModel;
  public depthUnit: string;
  public selectedLoadCase: SelectItem = null;

  public types = [
    { label: 'Burst', value: 'burstLeak' },
    { label: 'Collapse', value: 'collapse' },
    { label: 'Axial', value: 'axial' }
  ]

  public columnDefs: { header: string, field: string }[];
  public columnDefinitions = this.setDepths();
  public signalRChange: boolean;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.connectionSafetyFactorResultsStore = this._storeService.createStore(this.componentId, new ConnectionSafetyFactorResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.connectionSafetyFactorResultsStore.state.selectedMode || 'minimum';
    this.selectedResultDisplay = this.connectionSafetyFactorResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.connectionSafetyFactorResultsStore.state.selectedDepthView || 'md';
    this.includeCharacters = this.connectionSafetyFactorResultsStore.state.includeCharacters ?? true;
    this.selectedLoadCase = this.connectionSafetyFactorResultsStore.state.selectedLoadCase || null;
  }
  get componentId(): string {
    return this._componentId;
  }
  public connectionSafetyFactorResultsStore: Store;

  public selectedMode: string = 'minimum';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public includeCharacters: boolean = true;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _designFactorsService: DesignFactorsService,
    private _storeService: StoreService,
    private _wellConfigService: WellConfigService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.depthUnit = this.userUnits.longLengths;
    this.yAxisTitle = `MD (${this.userUnits.longLengths})`;

    // this.refreshResults(true, false, false);

    if (!this.previousView && this.connectionSafetyFactorResultsStore.state.selectedMode) {
      this.previousView = this.connectionSafetyFactorResultsStore.state?.selectedMode;
    } else {
      this.previousView = 'single'
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(true, false, false);
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS) {
      //TODO Does this need to reset to single?
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: null }));

      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true, false);
    }
  }

  private setPlotName(): string {
    if (this.connectionSafetyFactorResultsStore.state.selectedMode === 'single') {
      return 'connectionSafetyFactorsPlot - ' + this.selectedString + ' - ' + this.connectionSafetyFactorResultsStore.state.selectedMode + ' ' + this.connectionSafetyFactorResultsStore.state.selectedLoadCase.loadCaseName;
    } else {
      return 'connectionSafetyFactorsPlot - ' + this.selectedString + ' - ' + this.connectionSafetyFactorResultsStore.state.selectedMode + ' ' + this.connectionSafetyFactorResultsStore.state.selectedLoadCase.label;
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode?: boolean): Promise<void> {
    this.errorMsg = null;
    this.isLoading.set(isLoading);

    if ((!this.connectionSafetyFactorResults || calledFromSignalR) && !calledFromMode) {
            this._wellConfigService.getTubular().subscribe({
        next: (tubular) => {
          if (tubular != null) {
            const sources = [
              this._stressResultsService.getAxialResults(),
              this._stressResultsService.getConnectionSafetyFactorResults(),
              this._designFactorsService.getDesignFactors()
            ];

            forkJoin(sources).pipe(
              map(([axialResults, safetyFactorResults, designFactors]) => {
                const connectionSafetyFactorResults = safetyFactorResults;
                this.resultsBackup = safetyFactorResults;
                this.designFactors = designFactors.connection;
                this.axialResults = axialResults;
                if (connectionSafetyFactorResults.length === 0 || connectionSafetyFactorResults[0].loadCaseName === '') {
                  this.errorMsg = "Please calculate PERIVIS to see results";
                  this.columnDefs = [];
                  this.results = [];
                  this.connectionSafetyFactorResults = [];
                  this.isLoading.set(false);
                  return;
                }
                this.connectionSafetyFactorResults = safetyFactorResults;

                this.remapData();
              }),
              catchError(err => {
                this.errorMsg = "Well Configuration Missing: Please specify at least one string";
                this.isLoading.set(false);
                return err;
              })).subscribe();
          } else {
            this.isLoading.set(false);
            this.errorMsg = "Well Configuration Missing: Please specify at least one string";
          }
        }
      });
    } else {
      this.remapData();
    }
  }

  private remapData() {
    if (!this.connectionSafetyFactorResults) {
      return;
    }
    this.minimumResultType = this.connectionSafetyFactorResultsStore.state.selectedLoadCase?.value || this.connectionSafetyFactorResultsStore.state.selectedLoadCase;
    if (this.connectionSafetyFactorResultsStore.state.selectedMode == "minimum" && this.minimumResultType == null) {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Summary', value: 'summary' } }));
      this.minimumResultType = 'summary';
    }
    if (this.connectionSafetyFactorResultsStore.state.selectedMode == "single") {
      this.handleSingleSafetyFactorLoad(this.connectionSafetyFactorResults, true);
    } else if (this.connectionSafetyFactorResultsStore.state.selectedMode == "multiple") {
      this.handleMultiSafetyFactorLoad(this.connectionSafetyFactorResults, this.designFactors)
    } else if (this.connectionSafetyFactorResultsStore.state.selectedMode == 'minimum' && this.minimumResultType !== 'burstLeak' &&
      this.minimumResultType !== 'axial' && this.minimumResultType !== 'collapse') {
      this.handleMinimumSummary(this.connectionSafetyFactorResults, this.designFactors);
    } else {
      this.handleMinimum(this.connectionSafetyFactorResults, this.designFactors, this.minimumResultType);
    }
    this.signalRChange = true;
    this.plotName = this.setPlotName();
    this.isLoading.set(false);
  }

  setLoadCase(e): void {
    this.minimumResultType = e.value;

    if (this.connectionSafetyFactorResultsStore.state.selectedMode === "single") {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.connectionSafetyFactorResults;
    } else {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }

    if (this.connectionSafetyFactorResultsStore.state.selectedMode === 'minimum' && e.value?.value !== 'summary') {
      this.handleMinimum(this.resultsBackup, this.designFactors, e.value);
    }

    if (this.connectionSafetyFactorResultsStore.state.selectedMode === 'minimum' && e.value?.value === 'summary') {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    }
  }

  public onSelectedResultDisplayUpdated(e): void {
    if (e.value === 'plot' || e.value === 'grid') {
      this.selectedResultDisplay = e.value;
    }
    this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
    this.plotName = this.setPlotName();
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  public modeToggle(e) {
    const newMode = e.value;
    const comingFromMinimumOrSummary = this.previousView === 'minimum' || this.previousView === 'summary';
    const comingFromMultiple = this.previousView === 'multiple';
    // const comingFromSingle = this.previousView === 'single';

    // ✅ Reset result display when returning from minimum/summary to single
    if (newMode === 'single' && comingFromMinimumOrSummary) {
        this.connectionSafetyFactorResultsStore.update(state => ({
          ...state,
          selectedResultDisplay: 'plot'
        }));
    }

    // ✅ Update selectedMode
    this.connectionSafetyFactorResultsStore.update(state => ({
      ...state,
      selectedMode: newMode
    }));

    // ✅ Set selectedLoadCase depending on new mode
    let selectedLoadCaseUpdate = null;
    if (newMode === 'minimum') {
      selectedLoadCaseUpdate = { label: 'Summary', value: 'summary' };
    } else if (newMode === 'multiple') {
      selectedLoadCaseUpdate = { label: 'Barlow Burst', value: 'barlowBurst' };
    }

    if (selectedLoadCaseUpdate) {
      this.selectedLoadCase = selectedLoadCaseUpdate;
      this.connectionSafetyFactorResultsStore.update(state => ({
        ...state,
        selectedLoadCase: selectedLoadCaseUpdate
      }));
    }

    // ✅ Reset and remap data
    if (newMode === 'minimum' && !comingFromMultiple) {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    } else if (newMode === 'single') {
      this.remapData(); // Always remap for single
    }

    // ✅ Update axis title if leaving minimum/multiple
    if (newMode !== 'minimum') {
      this.xAxisTitle = 'Connection Safety Factor';
    }

    // ✅ Plot name and refresh logic
    const type = this.types.findIndex(
      x => x.value === this.connectionSafetyFactorResultsStore.state.selectedLoadCase
    ) > 0;

    this.plotName = this.setPlotName();
    this.refreshResults(
      type ? this.connectionSafetyFactorResultsStore.state.selectedLoadCase : { label: 'Barlow Burst', value: 'barlowBurst' },
      false,
      true
    );

    // ✅ Store previous view for next transition
    this.previousView = newMode;
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleSafetyFactorLoad(connectionSafetyFactorResults: any[], calledFromSignalR: boolean) {
    if (calledFromSignalR) {
      //TODO Might need to fix this
      // this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }
    this.minimumResultType = 'summary';
    this.columnDefs = [
      { header: 'Burst', field: 'burst' },
      { header: 'Collapse', field: 'collapse' },
      { header: 'Axial', field: 'axial' }
    ];

    this.columnDefinitions = this.setDepths();
    this.columnDefinitions.push(
      {
        header: 'Burst', field: 'burst',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Collapse', field: 'collapse',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Axial', field: 'axial',
        valueFormatter: (params: any) => params
      }
    );
    this.results = [];

    this.loadCases = connectionSafetyFactorResults?.map(lc => {
      lc.connectionSafetyFactorResults.forEach(element => {
        // Burst
        const designFactorsBurstCheck = this.connectionSafetyFactorResultsStore.state.includeCharacters && element['burst'] < this.getDesignFactor(this.designFactors, 'burstLeak') - 0.000001 ? ' *' : '';
        if (element['burst'].toString().includes('*')) {
          const burst = element['burst'].toString().substring(0, element['burst'].toString().indexOf(' '));
          element['burst'] = this.roundValue(burst, 3) + designFactorsBurstCheck;
        } else {
          element['burst'] = this.roundValue(element['burst'], 3);
        }

        // Collapse
        const designFactorsCollapseCheck = this.connectionSafetyFactorResultsStore.state.includeCharacters && element['collapse'] < this.getDesignFactor(this.designFactors, 'collapse') - 0.000001 ? ' *' : '';
        if (element['collapse'].toString().includes('*')) {
          const collapse = element['collapse'].toString().substring(0, element['collapse'].toString().indexOf(' '));
          element['collapse'] = this.roundValue(collapse, 3) + designFactorsCollapseCheck;
        } else {
          element['collapse'] = this.roundValue(element['collapse'], 3);
        }

        // Axial
        if (element['axial'].toString().includes('*')) {
          const axial = element['axial'].toString().substring(0, element['axial'].toString().indexOf(' '));
          element['axial'] = this.roundValue(axial, 3) + designFactorsCollapseCheck;
        } else {
          element['axial'] = this.roundValue(element['axial'], 3);
        }

      });
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.connectionSafetyFactorResultsStore.state.selectedLoadCase || this.previousView != 'single' || !this.connectionSafetyFactorResultsStore.state.selectedLoadCase.loadCaseName) {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.selectedLoadCase = this.loadCases[0];
      this.plotName = this.setPlotName();
    }
    this.previousView = 'single';
    this.results = this.loadCases.find(x => x.label == (this.connectionSafetyFactorResultsStore.state.selectedLoadCase?.loadCaseName ?? this.connectionSafetyFactorResultsStore.state.selectedLoadCase?.label)).value?.connectionSafetyFactorResults || [];
  }

  private handleMultiSafetyFactorLoad(connectionSafetyFactorResults: any[], connectionDesignFactors) {

    this.minimumResultType = 'summary';
    this.columnDefinitions = this.setDepths();
    this.columnDefs = [];
    this.results = [];
    let i = 0;
    this.loadCases = this.types;

    if (this.previousView !== 'multiple') {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }
    this.previousView = 'multiple';

    connectionSafetyFactorResults.forEach(load => {
      const loadName: string = load.loadCaseName;
      const loadNameCamel = toCamelCaseString(loadName);
      let selectedType = this.connectionSafetyFactorResultsStore.state.selectedLoadCase?.value ?? this.connectionSafetyFactorResultsStore.state.selectedLoadCase;

      this.setAxisNames(selectedType);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName, field: loadNameCamel,
        valueFormatter: (params: any) => params
      }
      );
      const backupArray = [];
      load.connectionSafetyFactorResults.forEach(loadResult => {
        if (selectedType == 'burstLeak') {
          selectedType = 'burst'
        }
        const result = {
          [loadNameCamel]: this.connectionSafetyFactorResultsStore.state.includeCharacters ? this.roundValue(loadResult[selectedType], 3) + (loadResult[selectedType] < (this.getDesignFactor(connectionDesignFactors, selectedType) - 0.000001) ? ' *' : '') : this.roundValue(loadResult[selectedType], 3),
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (i === 0) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });

      if (i === connectionSafetyFactorResults.length - 1) {
        this.addDesignFactor(selectedType, connectionDesignFactors);
      }
      i++;
    });
    this.isLoading.set(false);
  }

  private handleMinimumSummary(connectionSafetyFactorResults: any[], designFactors) {
    // if (this.previousView !== 'summary') {
    //   this.selectedLoadCase = 'summary';
    // }
    this.previousView = 'summary';
    this.columnDefinitions = [];
    this.columnDefs = [];
    this.results = [];
    this.loadCases = this.types;
    this.loadCases = this.loadCases.filter(x => x.label !== 'Summary');
    this.loadCases.unshift({ label: 'Summary', value: 'summary' });

    this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: 'grid' }));
    this.selectedResultDisplay = 'grid';

    this.columnDefinitions = [
      {
        header: 'Load Name', field: 'loadName',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Burst', field: 'burstLeak',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Collapse', field: 'collapse',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Axial', field: 'axial',
        valueFormatter: (params: any) => params
      },
    ];

    connectionSafetyFactorResults.forEach(load => {
      if (!load.connectionSafetyFactorResults) {
        this.errorMsg = null;
        this.columnDefs = [];
        this.results = [];
        this.isLoading.set(false);
        return;
      }

      let smallestBurst = 10000;
      let smallestAxial = 10000;
      let smallestCollapse = 10000;
      const firstDepth = load.connectionSafetyFactorResults[0].measuredDepth;
      let depthBurst = firstDepth;
      let depthAxial = firstDepth;
      let depthCollapse = firstDepth;
      load.connectionSafetyFactorResults.forEach(element => {
        if (element['burst'] < smallestBurst) {
          smallestBurst = element['burst'];
          depthBurst = element.measuredDepth.toFixed(3);
        }

        if (element['axial'] < smallestAxial) {
          smallestAxial = element['axial'];
          depthAxial = element.measuredDepth.toFixed(3);
        }

        if (element['collapse'] < smallestCollapse) {
          smallestCollapse = element['collapse'];
          depthCollapse = element.measuredDepth.toFixed(3);
        }
      });

      if (this.results.length < 1) {
        this.results.push({
          loadName: 'Design Factor',
          burstLeak: designFactors['burstLeak'],
          collapse: designFactors['collapse'],
          axial: Math.min(designFactors['tension'], designFactors['compression']),
        });
      }

      const tolerance = this.userUnits.longLengths == 'ft' ? 0.001 : 0.01;
      const loadIndex = this.axialResults.findIndex(x => x.loadCaseName == load.loadCaseName);
      const depthIndex = this.axialResults[loadIndex].axialResults.findIndex(x => Math.abs(x.measuredDepth - depthAxial) < tolerance);
      const axialForceValue = this.axialResults[loadIndex].axialResults[depthIndex].axialLoad;
      const tensionCompression = axialForceValue < 0 ? ' - Compression' : '';
      const depthUnit = this.userUnits.longLengths + "MD";

      this.results.push({
        loadName: load.loadCaseName,
        burstLeak: this.connectionSafetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestBurst, 3) + (smallestBurst < this.getDesignFactor(designFactors, 'burstLeak') - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthBurst, 10) + ` ${depthUnit}` : this.roundValue(smallestBurst, 3),
        collapse: this.connectionSafetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestCollapse, 3) + (smallestCollapse < this.getDesignFactor(designFactors, 'collapse') - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthCollapse, 10) + ` ${depthUnit}` : this.roundValue(smallestCollapse, 3),
        axial: this.connectionSafetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestAxial, 3) + (smallestAxial < this.getDesignFactor(designFactors, 'axial') - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthAxial, 10) + ` ${depthUnit}` + tensionCompression : this.roundValue(smallestAxial, 3)
      });
    });
    if (!this.connectionSafetyFactorResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.connectionSafetyFactorResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }
    this.signalRChange = !this.signalRChange;
    this.isLoading.set(false);
  }

  private getDesignFactor(designFactors: any, selectedType) {
    let df = selectedType;
    if (df === 'axial') {
      df = designFactors['tension'] < designFactors['compression'] ? designFactors['tension'] : designFactors['compression'];
    } else if (df === 'collapse') {
      df = designFactors['collapse'];
    } else if ((this.connectionSafetyFactorResultsStore.state.selectedMode === 'minimum' && df.startsWith('burst')) ||
      this.connectionSafetyFactorResultsStore.state.selectedMode === 'multiple' && df === 'burst') {
      df = designFactors['burstLeak'];
    }

    return df;
  }

  private handleMinimum(safetyFactorResults: any[], designFactors, type) {
    this.previousView = 'minimum';
    this.columnDefinitions = [];
    this.columnDefs = [];
    this.results = [];
    this.loadCases = this.types;
    this.loadCases = this.loadCases.filter(x => x.label !== 'Summary');
    this.loadCases.unshift({ label: 'Summary', value: 'summary' });

    let resolvedType = type.value ?? type;
    if (resolvedType === 'burstLeak') {
      resolvedType = 'burst';
    }
    const selectedType = resolvedType;
    this.setAxisNames(selectedType);

    this.connectionSafetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: 'grid' }));
    this.selectedResultDisplay = 'grid';

    const typeLabel = this.types.find(t => t.value === selectedType || (selectedType === 'burst' && t.value === 'burstLeak'))?.label ?? selectedType;
    const depthUnit = this.userUnits.longLengths + "MD";
    const dfField = selectedType === 'burst' ? 'burstLeak' : selectedType;

    this.columnDefinitions = [
      {
        header: 'Load Name', field: 'loadName',
        valueFormatter: (params: any) => params
      },
      {
        header: typeLabel, field: selectedType,
        valueFormatter: (params: any) => params
      }
    ];

    this.results.push({
      loadName: 'Design Factor',
      [selectedType]: this.getDesignFactor(designFactors, dfField)
    });

    safetyFactorResults.forEach(load => {
      let smallestValue = 10000;
      let depthMd = 0;
      if (!load.connectionSafetyFactorResults || load.connectionSafetyFactorResults.length === 0) {
        return;
      }
      load.connectionSafetyFactorResults.forEach(element => {
        if (element[selectedType] < smallestValue) {
          smallestValue = element[selectedType];
          depthMd = element.measuredDepth;
        }
      });

      const designFactorCheck = this.connectionSafetyFactorResultsStore.state.includeCharacters && smallestValue < this.getDesignFactor(designFactors, selectedType) - 0.000001 ? ' *' : '';

      this.results.push({
        loadName: load.loadCaseName,
        [selectedType]: this.connectionSafetyFactorResultsStore.state.includeCharacters
          ? this.roundValue(smallestValue, 3) + designFactorCheck + ' - @ - ' + this.formatDecimal(depthMd, 10) + ` ${depthUnit}`
          : this.roundValue(smallestValue, 3)
      });
    });

    this.isLoading.set(false);
  }

  private setDepths() {
    return [
      {
        header: `MD (${this.depthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.depthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
    ]
  }

  private setAxisNames(selectedType: any) {
    switch (selectedType) {
      case 'burstLeak': {
        this.xAxisTitle = 'Burst Safety Factor';
        break;
      }
      case 'collapse': {
        this.xAxisTitle = 'Collapse Safety Factor';
        break;
      }
      case 'axial': {
        this.xAxisTitle = 'Axial Safety Factor';
        break;
      }
      default: {
        break;
      }
    }
  }

  private roundValue(value, decimalPoints) {
    let fixed = decimalPoints || 0;
    fixed = Math.pow(10, fixed);
    return Math.floor(value * fixed) / fixed;
  }

  private addDesignFactor(selectedType, connectionDesignFactors) {
    let gridDesignFactorName = '';
    let df = selectedType;
    if (df === 'axial') {
      df = this.designFactors['tension'] < this.designFactors['compression'] ? 'tension' : 'compression';
      gridDesignFactorName = 'Axial DF';
    } else if (df === 'collapse') {
      gridDesignFactorName = 'Collapse DF';
      df = 'collapse';
    } else if (df.startsWith('burst')) {
      gridDesignFactorName = 'Burst DF';
      df = 'burstLeak';
    }

    this.columnDefs.push({ header: gridDesignFactorName, field: selectedType });
    this.columnDefinitions.push(
      {
        header: gridDesignFactorName, field: selectedType,
        valueFormatter: (params: any) => this.formatDecimal(params, 1000)
      }
    );
    this.results = this.results.map(x => {
      const designFactor = {
        [selectedType]: connectionDesignFactors[df],
        measuredDepth: x.measuredDepth,
        trueVerticalDepth: x.trueVerticalDepth
      }
      return { ...x, ...designFactor }
    });
  }

  public toggleCharacterView(e) {
    this.includeCharacters = e.checked;
    this.connectionSafetyFactorResultsStore.update(state => ({ ...state, includeCharacters: e.checked }));

    if (this.connectionSafetyFactorResultsStore.state.selectedMode == "single") {
      this.handleSingleSafetyFactorLoad(this.resultsBackup, false);
    } else if (this.connectionSafetyFactorResultsStore.state.selectedMode == 'multiple') {
      this.handleMultiSafetyFactorLoad(this.resultsBackup, this.designFactors)
    } else if (this.connectionSafetyFactorResultsStore.state.selectedMode == 'minimum' && this.minimumResultType !== 'burstLeak' && !this.minimumResultType?.startsWith('collapse') &&
      this.minimumResultType !== 'axial') {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    }

    if (this.connectionSafetyFactorResultsStore.state.selectedMode == 'minimum' && this.minimumResultType == 'burstLeak') {
      this.handleMinimum(this.resultsBackup, this.designFactors, this.connectionSafetyFactorResultsStore.state.selectedLoadCase);
    }
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
